/* Asking the shell's Copilot drawer to open, from a page that does not own it.
 *
 * **An event rather than shared state**, because the drawer belongs to the shell and the pages that
 * offer "Ask Copilot" are features the shell does not import: a context provider would put the
 * drawer's state above every route for the sake of one button per screen, and a prop would have to
 * travel through the router to reach it. A window event is one sender and one listener, and a page
 * rendered without a shell — every screen test — simply has nobody listening.
 *
 * **A question pre-fills the composer and is never sent.** Opening the drawer is free; a question
 * costs a completion, and nobody should spend one by pressing a button that said "Ask" before they
 * had read what it would ask. The starter questions follow the same rule.
 */

const OPEN_EVENT = 'lumiq:copilot-open';

export interface CopilotOpenRequest {
  /** What to put in the composer, unsent. Absent opens the drawer with whatever was there. */
  readonly question?: string | undefined;
}

export function openCopilot(request: CopilotOpenRequest = {}): void {
  window.dispatchEvent(
    new CustomEvent<CopilotOpenRequest>(OPEN_EVENT, { detail: request }),
  );
}

/** Subscribes to open requests; returns the unsubscribe. */
export function onCopilotOpenRequest(
  handler: (request: CopilotOpenRequest) => void,
): () => void {
  const listener = (event: Event): void => {
    handler(event instanceof CustomEvent ? (event.detail as CopilotOpenRequest) : {});
  };
  window.addEventListener(OPEN_EVENT, listener);
  return () => {
    window.removeEventListener(OPEN_EVENT, listener);
  };
}
